import { useState, useEffect, useCallback } from 'react'
import * as contractsApi from '@/services/api/contracts'
import type { Contract, PaginationMeta } from '@/services/api/contracts'
import { useDebounce } from './useDebounce'

interface UseContractsParams {
  type: 'buy' | 'sale' | null
  search?: string
  supplier?: string
  page?: number
  limit?: number
  enabled?: boolean
}

export function useContracts(params: UseContractsParams) {
  const { type, search = '', supplier = '', page = 1, limit = 10, enabled = true } = params
  const debouncedSearch = useDebounce(search, 400)
  const [contracts, setContracts] = useState<Contract[]>([])
  const [meta, setMeta] = useState<PaginationMeta | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchContracts = useCallback(async () => {
    if (!enabled) {
      setLoading(false)
      return
    }
    try {
      setLoading(true)
      setError(null)
      const result = await contractsApi.getContracts({
        page,
        limit,
        search: debouncedSearch.trim() || undefined,
        type: type || undefined,
        supplier: supplier && supplier !== 'Select Supplier' ? supplier : undefined,
      })
      setContracts(result.contracts)
      setMeta(result.meta)
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to fetch contracts')
      setContracts([])
      setMeta(null)
    } finally {
      setLoading(false)
    }
  }, [enabled, type, debouncedSearch, supplier, page, limit])

  useEffect(() => {
    fetchContracts()
  }, [fetchContracts])

  return { contracts, meta, loading, error, refetch: fetchContracts }
}
